import { Typography, Container } from '@material-ui/core';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { UrlParams } from 'src/modules/shared/types';
import { useGetTableOfContentQuery } from 'src/store/table-of-contents/tableOfContentsApi';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import {
  setClickedOnNavgationBarIndex,
  setExpandedIndex,
} from 'src/store/table-of-contents/tableOfContentsSlice';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';

import { setBookId } from 'src/modules/shared/slices/book/bookEntity';
import {
  SampleLessonButton,
  TOCChapterTitle,
  TOCChapterWrapper,
  TrajectoriesButton,
} from './TableOfContentsChapterStyled';
import { TrajectoryAccordion } from '../TrajectoryAccordion/TrajectoryAccordion';

export const TableOfContentsChapter: FC = () => {
  const dispatch = useAppDispatch();
  let history = useHistory();
  let params = useParams<UrlParams>();
  const { bookId } = params;
  const expandedIndex = useAppSelector((state) => state.tableOfContents.expandedIndex);
  const clickedOnNavBar = useAppSelector((state) => state.tableOfContents.clickedOnNavBar);
  const reduxBookId = useAppSelector((state) => state.books.bookId);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const { data } = useGetTableOfContentQuery(bookId ?? '', {
    skip: !bookId,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (bookId && bookId !== reduxBookId) {
      dispatch(setBookId(bookId));
    }
  }, [bookId, dispatch, reduxBookId]);

  const handleSampleLesson = useCallback(
    (trajectoryId: string, chapterId: string) => {
      dispatch(setClickedOnNavgationBarIndex(chapterId));
      history.push(
        `/nl-be/boekentas/${bookId}/hoofdstukken/${chapterId}/traject/${trajectoryId}/0/opbouw/`,
        {
          chapterId,
          trajectoryId,
          bookId,
        }
      );
    },
    [bookId, history, dispatch]
  );

  const handleShowChaptersClick = useCallback(
    (id: string) => {
      let newId: string = id;
      if (id === expandedIndex) {
        newId = '';
      }
      dispatch(setExpandedIndex(newId));
    },
    [dispatch, expandedIndex]
  );

  useEffect(() => {
    if (expandedIndex) {
      dispatch(setClickedOnNavgationBarIndex(''));
    }
  }, [dispatch, expandedIndex]);

  return (
    <>
      <div style={{ display: 'flex', gap: 16, flexDirection: 'column', marginTop: 50 }}>
        {data?.chapters.map((chapter) => {
          const isSelected = chapter.id === clickedOnNavBar;
          const isExpanded = chapter.id === expandedIndex;
          return (
            <div key={chapter.id}>
              <TOCChapterWrapper
                id={`chapter-${chapter.id}`}
                bgcolor={chapter.color}
                isexpanded={isExpanded ? 'true' : 'false'}
                selected={isSelected ? 'true' : 'false'}
              >
                <Container
                  style={{
                    padding: 0,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="body1"
                    style={{ fontSize: '14px', fontWeight: 400, color: '#FFFFFF' }}
                  >
                    Hoofdstuk {chapter.number}
                  </Typography>
                  <TOCChapterTitle
                    variant="h2"
                    selected={isSelected && !isExpanded ? 'true' : 'false'}
                  >
                    {chapter.title}
                  </TOCChapterTitle>
                  {chapter.demoInformation && (
                    <Typography
                      style={{
                        color: '#FFFFFF',
                        fontWeight: 300,
                        margin: '15px 0',
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: chapter.demoInformation }} />
                    </Typography>
                  )}
                  <div style={{ display: 'flex', gap: 12, marginTop: 16, flexDirection: 'row' }}>
                    {chapter?.sampleTrajectory?.id && (
                      <SampleLessonButton
                        disabled={isDisabled}
                        custombgcolor={chapter.color}
                        variant="contained"
                        size="small"
                        onClick={() => handleSampleLesson(chapter.sampleTrajectory.id, chapter.id)}
                        startIcon={
                          <FontAwesomeSpriteIcon
                            icon={SpriteIconsLight.ARROW_DOWN}
                            iconSize="xs"
                            color={'#FFFFFF'}
                          />
                        }
                      >
                        <Typography style={{ fontWeight: 600 }}>NAAR VOORBEELDLES</Typography>
                      </SampleLessonButton>
                    )}
                    <TrajectoriesButton
                      disabled={isDisabled}
                      size="small"
                      variant="outlined"
                      onClick={() => handleShowChaptersClick(chapter.id)}
                    >
                      inhoudstafel
                    </TrajectoriesButton>
                  </div>
                </Container>
              </TOCChapterWrapper>
              {isExpanded && (
                <TrajectoryAccordion chapter={chapter} setIsDisabled={setIsDisabled} />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};
